// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 63f271c4-4b2d-40ed-a19a-3723ae794328

// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, Chip, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip,
  IconButton, Divider,
} from '@mui/material';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { getConfig } from './config';
import { getIdToken, getSession } from './auth';
import HelpBubble from './HelpBubble';

// Per-client FinOps connections (backend/sql/020_finops_connection.sql,
// backend/lambda/finops.js). A connection names a client's OWN AWS payer
// account and, once verified, proves Primordia can read its consolidated
// spend — read-only, revocable by deleting the stack in their account at any
// time. Nothing here handles a credential directly: the whole flow is "we
// hand you a CloudFormation quick-create link that already has our template
// and the required parameters filled in; you review and click Create in YOUR
// OWN console session; we then attempt sts:AssumeRole and tell you whether it
// worked." Zero CLI, zero pasted ARNs, zero secrets crossing the wire in
// either direction — see deploy/finops-payer-role.yaml for what the stack
// actually grants (read-only cost/recommendation APIs, nothing else).
//
// One workspace holds at most one connection per provider (UNIQUE(tenant_id,
// provider) in the migration), so this renders as a fixed pair of status
// cards — AWS and Azure — never a table, and one tenant may hold BOTH.
//
// AZURE = LIGHTHOUSE (2026-08-17). The Azure card runs the same
// nothing-crosses-the-wire flow with even less held than AWS's role: the
// client deploys our ARM template (deploy/finops-lighthouse.json) into each
// subscription, which delegates it to Optimum's managing tenant with Cost
// Management Reader for our service principal. No client secret ever exists
// on either side, and revocation is entirely theirs — delete the
// registration assignment under the portal's Service providers blade.
// Unlike the AWS quick-create link, the Azure portal deep link cannot
// reliably pre-fill template parameters, so the two managing-side values
// (managing tenant id + principal id) arrive in the connection payload and
// are shown copy-paste-ready beside the button. What the connection ROW
// stores is the CLIENT'S directory (tenant) id — verify works by
// enumeration: delegated subscriptions appear in OUR principal's own
// subscription listing carrying that tenant id.
//
// V1 boundary: connection + verify only. The FinOps screen still reads AWS
// spend exclusively — a workspace whose only connection is Azure shows
// connected HERE while FinOps keeps its honest not-connected-for-AWS state,
// until the Azure per-client read path (the next stream) lands.

const HEAD = 'sectionHeader.main';

const STATUS_META = {
  connected: { label: 'Connected', color: 'success', Icon: CloudDoneIcon },
  pending: { label: 'Pending', color: 'warning', Icon: CloudOffIcon },
  error: { label: 'Error', color: 'error', Icon: ErrorOutlineIcon },
  none: { label: 'Not connected', color: 'default', Icon: CloudOffIcon },
};

function fmtDateTime(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

// An Azure directory (tenant) id — a bare GUID. Mirrors the backend's isGuid
// in backend/lambda/finops.js; the server re-validates regardless.
const GUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

// A copy-paste-ready managing-side value beside the Deploy-to-Azure button.
// `value` may be absent (the principal-id SSM parameter not published yet) —
// shown honestly as unavailable rather than an empty copyable string.
function CopyValue({ label, value, onCopy }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>{label}:</Typography>
      {value ? (
        <>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{value}</Typography>
          <Tooltip title={`Copy ${label.toLowerCase()}`}>
            <IconButton size="small" onClick={() => onCopy(label, value)}>
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary"><i>not published for this stage yet</i></Typography>
      )}
    </Stack>
  );
}

// tenantId is the workspace currently in view — the same value AdminPortal
// already threads to every other tab (FieldsTab, ListsAdmin, TrashAdmin).
export default function CloudConnections({ tenantId }) {
  const me = getSession();
  const iAmMaster = !!me && (me.groups || []).includes('OptimumAdmins');
  const iAmAdmin = iAmMaster || (me && me.roleId === 'admin');

  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(''); // 'connect' | 'verify:<id>' | 'delete:<id>' | ''
  const [connectDialog, setConnectDialog] = useState(null); // { provider, existing, payerAccountId }
  const [confirmDelete, setConfirmDelete] = useState(null); // connection row

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}`, ...(options.headers || {}) },
    });
    const text = await res.text();
    // Same "an error envelope is often HTML, not JSON" guard as every other
    // admin screen's api() — CLAUDE.md records why: a raw JSON.parse threw
    // before the real status could ever be reported.
    let data = {};
    let parseFailed = false;
    try { data = text ? JSON.parse(text) : {}; } catch (e) { parseFailed = true; }
    if (!res.ok) throw new Error(data.error || data.message || `request failed (${res.status})`);
    if (parseFailed) throw new Error(`The API returned ${res.status} with a body that is not JSON.`);
    return data;
  }, []);

  const load = useCallback(async () => {
    if (!tenantId || tenantId === 'optimum') { setLoading(false); return; }
    setLoading(true); setErr('');
    try {
      const rows = await api(`/finops/connections?tenantId=${encodeURIComponent(tenantId)}`);
      setConnections(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [api, tenantId]);

  useEffect(() => { load(); }, [load]);

  const byProvider = useMemo(() => {
    const m = { aws: null, azure: null };
    connections.forEach((c) => { if (c.provider === 'aws' || c.provider === 'azure') m[c.provider] = c; });
    return m;
  }, [connections]);

  const openConnectDialog = (provider, existing) => setConnectDialog({
    provider,
    existing: existing || null,
    payerAccountId: (existing && existing.payerAccountId) || '',
  });

  const submitConnect = async () => {
    const provider = connectDialog.provider;
    const payerAccountId = (connectDialog.payerAccountId || '').trim();
    if (provider === 'aws' && !/^\d{12}$/.test(payerAccountId)) {
      setErr('AWS payer account id must be exactly 12 digits.');
      return;
    }
    if (provider === 'azure' && !GUID_RE.test(payerAccountId)) {
      setErr('Azure tenant id must be a GUID, e.g. 12345678-90ab-cdef-1234-567890abcdef.');
      return;
    }
    setBusy('connect'); setErr(''); setMsg('');
    try {
      const result = await api('/finops/connections', {
        method: 'POST',
        body: JSON.stringify({ tenantId, provider, payerAccountId }),
      });
      setConnectDialog(null);
      await load();
      const url = result.connection && result.connection.quickCreateUrl;
      if (provider === 'azure') {
        // No auto-open, unlike AWS: the Azure portal cannot pre-fill the
        // template parameters, so the admin needs the copy-paste values on
        // the card FIRST, then the Deploy to Azure button.
        setMsg('Connection saved. Use "Deploy to Azure" on the card below — it opens the Azure portal with '
          + 'our Lighthouse template; paste the two values shown beside the button, pick the subscription, '
          + 'and click Create. Repeat once per subscription, then come back here and press Verify.');
      } else if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        setMsg('A new tab opened to AWS CloudFormation with our template and the required parameters '
          + 'already filled in. Review the stack, click "Create stack" in YOUR OWN AWS console, wait for '
          + 'CREATE_COMPLETE, then come back here and press Verify.');
      } else {
        setMsg('Connection saved, but the quick-create link is not available yet '
          + '(the template has not been published for this stage). Ask an Optimum engineer to deploy it, '
          + 'then reopen this screen.');
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy('');
    }
  };

  const reopenQuickCreate = (conn) => {
    if (!conn.quickCreateUrl) {
      setErr(`The ${conn.provider === 'azure' ? 'Deploy to Azure' : 'quick-create'} link is not available yet `
        + '(template not published for this stage).');
      return;
    }
    window.open(conn.quickCreateUrl, '_blank', 'noopener,noreferrer');
  };

  const verify = async (conn) => {
    setBusy(`verify:${conn.id}`); setErr(''); setMsg('');
    try {
      const result = await api('/finops/connections/verify', { method: 'POST', body: JSON.stringify({ id: conn.id }) });
      await load();
      const row = result.connection;
      if (row && row.status === 'connected') {
        // delegatedSubscriptionCount rides the verify RESPONSE only — the
        // backend deliberately persists nothing about it.
        const n = row.delegatedSubscriptionCount;
        setMsg(conn.provider === 'azure'
          ? `Verified — ${n === 1 ? '1 subscription is' : `${n} subscriptions are`} delegated to Optimum's `
            + 'managing tenant with read-only cost visibility.'
          : 'Verified — Primordia can read this account’s consolidated cost data.');
      } else {
        setMsg(`Not connected yet: ${(row && row.lastError)
          || (conn.provider === 'azure' ? 'no delegated subscriptions were found.' : 'the role could not be assumed.')}`);
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy('');
    }
  };

  const disconnect = async () => {
    const conn = confirmDelete;
    setBusy(`delete:${conn.id}`); setErr(''); setMsg('');
    try {
      await api(`/finops/connections?id=${encodeURIComponent(conn.id)}`, { method: 'DELETE' });
      setConfirmDelete(null);
      await load();
      setMsg(conn.provider === 'azure'
        ? 'Disconnected Azure. Primordia will no longer look for this tenant’s delegated subscriptions; to '
          + 'fully revoke access, also delete the "Optimum Primordia FinOps" offer under Service providers '
          + 'in your own Azure portal.'
        : 'Disconnected AWS. Primordia can no longer read this account’s cost data; delete the stack in '
          + 'the client’s own AWS console to fully revoke the role, too.');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy('');
    }
  };

  const copyValue = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setMsg(`${label} copied to the clipboard.`);
    } catch (e) {
      setErr('Copy failed — select the value and copy it manually.');
    }
  };

  if (!tenantId || tenantId === 'optimum') {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">
          Select a client workspace to manage its cloud connections. The Optimum master workspace holds no
          connections of its own — it has read access to every client’s spend through the connection
          made from their own screen, not a connection of its own.
        </Alert>
      </Box>
    );
  }

  if (!iAmAdmin) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">
          Cloud connections are admin-only. Ask your workspace administrator (or Optimum) to connect an
          AWS account before FinOps data can appear for this workspace.
        </Alert>
      </Box>
    );
  }

  const aws = byProvider.aws;
  const awsMeta = STATUS_META[(aws && aws.status) || 'none'];
  const azure = byProvider.azure;
  const azureMeta = STATUS_META[(azure && azure.status) || 'none'];

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD }}>Cloud connections</Typography>
        <HelpBubble topic="cloudConnections" />
        <IconButton onClick={load} title="Refresh" size="small"><RefreshIcon fontSize="small" /></IconButton>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Connect this workspace’s own cloud accounts so Primordia can read their spend — read-only, and
        revocable at any time from your own console. Nothing is installed and no credential is ever typed
        into Primordia: on AWS you create a read-only role stack in your own console session; on Azure you
        delegate subscriptions through Azure Lighthouse, where no secret exists on either side and
        revocation is a single delete in your own portal.
      </Typography>

      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}

      {loading ? (
        <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
      ) : (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          {/* ---------------- AWS ---------------- */}
          <Paper sx={{ p: 2, flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD, flex: 1 }}>Amazon Web Services</Typography>
              <Chip size="small" icon={<awsMeta.Icon fontSize="small" />} label={awsMeta.label} color={awsMeta.color} />
            </Stack>

            {aws ? (
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Payer account: <b>{aws.payerAccountId || '—'}</b>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last verified: {fmtDateTime(aws.lastVerifiedAt) || 'never'}
                </Typography>
                {/* Showback V2 (024_finops_tag_key.sql) — read-only here; the
                    key is set from the FinOps screen's Showback section. */}
                {aws.appTagKey && (
                  <Typography variant="body2" color="text.secondary">
                    Application tag key: <b>{aws.appTagKey}</b> (set on the FinOps screen — tags spend
                    to applications inside shared accounts)
                  </Typography>
                )}
                {aws.status === 'error' && aws.lastError && (
                  <Alert severity="error" sx={{ mt: 1 }}>{aws.lastError}</Alert>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Not connected yet. Connecting takes about two minutes in your own AWS console — no CLI,
                no keys to generate.
              </Typography>
            )}

            <Divider sx={{ mb: 2 }} />

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {!aws && (
                <Button
                  variant="contained" size="small"
                  disabled={busy === 'connect'}
                  onClick={() => openConnectDialog('aws', null)}
                >
                  {busy === 'connect' ? 'Connecting…' : 'Connect'}
                </Button>
              )}
              {aws && (
                <>
                  <Tooltip title="Reopen the CloudFormation quick-create link in a new tab">
                    <span>
                      <Button
                        size="small" variant="outlined" startIcon={<OpenInNewIcon fontSize="small" />}
                        onClick={() => reopenQuickCreate(aws)}
                      >
                        Open quick-create link
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    size="small" variant="contained"
                    disabled={busy === `verify:${aws.id}`}
                    onClick={() => verify(aws)}
                  >
                    {busy === `verify:${aws.id}` ? 'Verifying…' : 'Verify'}
                  </Button>
                  <Button size="small" variant="text" onClick={() => openConnectDialog('aws', aws)}>
                    Edit payer account
                  </Button>
                  <Button
                    size="small" color="error" variant="text" startIcon={<LinkOffIcon fontSize="small" />}
                    onClick={() => setConfirmDelete(aws)}
                  >
                    Disconnect
                  </Button>
                </>
              )}
            </Stack>
          </Paper>

          {/* ---------------- Azure (Lighthouse) ---------------- */}
          <Paper sx={{ p: 2, flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD, flex: 1 }}>Microsoft Azure</Typography>
              <Chip size="small" icon={<azureMeta.Icon fontSize="small" />} label={azureMeta.label} color={azureMeta.color} />
            </Stack>

            {azure ? (
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Directory (tenant) id: <b>{azure.payerAccountId || '—'}</b>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last verified: {fmtDateTime(azure.lastVerifiedAt) || 'never'}
                </Typography>
                {azure.status === 'error' && azure.lastError && (
                  <Alert severity="error" sx={{ mt: 1 }}>{azure.lastError}</Alert>
                )}
                {/* The two template parameters the portal cannot be handed in
                    the deep link — pasted by the admin into the deployment
                    form. Values come from the connection payload
                    (managing-side SSM), never typed in by the client. */}
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Paste these into the Azure deployment form:
                  </Typography>
                  <CopyValue label="Managing tenant id" value={azure.managingTenantId} onCopy={copyValue} />
                  <CopyValue label="Principal id" value={azure.principalId} onCopy={copyValue} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Deploy once per subscription — the portal’s scope picker chooses which subscription each
                    deployment delegates. Verify below counts every subscription it can see.
                  </Typography>
                </Box>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Not connected yet. Connecting uses Azure Lighthouse: you deploy a small template into each
                subscription from your own Azure portal — read-only cost visibility, no secret to create,
                and you can revoke it at any time under Service providers.
              </Typography>
            )}

            <Divider sx={{ mb: 2 }} />

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {!azure && (
                <Button
                  variant="contained" size="small"
                  disabled={busy === 'connect'}
                  onClick={() => openConnectDialog('azure', null)}
                >
                  {busy === 'connect' ? 'Connecting…' : 'Connect'}
                </Button>
              )}
              {azure && (
                <>
                  <Tooltip title="Open the Azure portal with our Lighthouse template in a new tab">
                    <span>
                      <Button
                        size="small" variant="outlined" startIcon={<OpenInNewIcon fontSize="small" />}
                        onClick={() => reopenQuickCreate(azure)}
                      >
                        Deploy to Azure
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    size="small" variant="contained"
                    disabled={busy === `verify:${azure.id}`}
                    onClick={() => verify(azure)}
                  >
                    {busy === `verify:${azure.id}` ? 'Verifying…' : 'Verify'}
                  </Button>
                  <Button size="small" variant="text" onClick={() => openConnectDialog('azure', azure)}>
                    Edit tenant id
                  </Button>
                  <Button
                    size="small" color="error" variant="text" startIcon={<LinkOffIcon fontSize="small" />}
                    onClick={() => setConfirmDelete(azure)}
                  >
                    Disconnect
                  </Button>
                </>
              )}
            </Stack>
          </Paper>
        </Stack>
      )}

      {/* ---------------- connect / edit account ---------------- */}
      <Dialog open={!!connectDialog} onClose={() => !busy && setConnectDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, color: HEAD }}>
          {connectDialog && connectDialog.provider === 'azure'
            ? (connectDialog.existing ? 'Edit Azure tenant id' : 'Connect Azure')
            : (connectDialog && connectDialog.existing ? 'Edit AWS payer account' : 'Connect AWS')}
        </DialogTitle>
        {connectDialog && connectDialog.provider === 'aws' && (
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Enter the 12-digit AWS account id of your organization’s <b>payer (management) account</b> —
              the one Cost Explorer reports consolidated spend from. Primordia only ever reads it through a
              role you create there; nothing is written to your account.
            </Typography>
            <TextField
              fullWidth size="small" label="AWS payer account id" placeholder="123456789012"
              value={connectDialog.payerAccountId}
              onChange={(e) => setConnectDialog((d) => ({ ...d, payerAccountId: e.target.value.replace(/[^0-9]/g, '').slice(0, 12) }))}
              helperText="12 digits, no dashes"
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              After saving, a new tab opens to AWS CloudFormation with our read-only role template and the
              required parameters already filled in. Click <b>Create stack</b> in your own console, wait for
              it to finish, then come back here and press <b>Verify</b>.
            </Alert>
          </DialogContent>
        )}
        {connectDialog && connectDialog.provider === 'azure' && (
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Enter your organization’s <b>Azure directory (tenant) id</b> — the GUID that identifies your
              Azure AD tenant. Find it at <b>portal.azure.com &rarr; Microsoft Entra ID &rarr; Overview
              &rarr; Tenant ID</b>. Primordia uses it only to recognize which delegated subscriptions are
              yours; no credential of yours is ever created or stored.
            </Typography>
            <TextField
              fullWidth size="small" label="Azure directory (tenant) id"
              placeholder="12345678-90ab-cdef-1234-567890abcdef"
              value={connectDialog.payerAccountId}
              onChange={(e) => setConnectDialog((d) => ({
                ...d,
                payerAccountId: e.target.value.replace(/[^0-9a-fA-F-]/g, '').slice(0, 36).toLowerCase(),
              }))}
              error={!!connectDialog.payerAccountId && !GUID_RE.test(connectDialog.payerAccountId.trim())}
              helperText="A GUID: 8-4-4-4-12 hex characters"
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              After saving, the Azure card shows a <b>Deploy to Azure</b> button plus the two values to
              paste into the deployment form. Deploy the template <b>once per subscription</b> (the
              portal’s scope picker chooses which), wait a few minutes for Lighthouse to propagate, then
              come back here and press <b>Verify</b>.
            </Alert>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setConnectDialog(null)} disabled={busy === 'connect'}>Cancel</Button>
          <Button
            variant="contained" onClick={submitConnect}
            disabled={busy === 'connect' || !connectDialog || !connectDialog.payerAccountId}
          >
            {busy === 'connect'
              ? 'Saving…'
              : (connectDialog && connectDialog.provider === 'azure' ? 'Save' : 'Save & open AWS console')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- disconnect confirm ---------------- */}
      <Dialog open={!!confirmDelete} onClose={() => !busy && setConfirmDelete(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, color: HEAD }}>
          Disconnect {confirmDelete && confirmDelete.provider === 'azure' ? 'Azure' : 'AWS'}
        </DialogTitle>
        {confirmDelete && confirmDelete.provider !== 'azure' && (
          <DialogContent dividers>
            <Typography sx={{ mb: 2 }}>
              Remove this workspace’s AWS connection? Primordia will immediately stop being able to read
              account <b>{confirmDelete.payerAccountId || confirmDelete.id}</b>’s cost data.
            </Typography>
            <Alert severity="warning">
              This only removes Primordia’s record of the connection. To fully revoke access, also delete
              the <code>primordia-finops-read</code> stack from that account’s own CloudFormation console.
            </Alert>
          </DialogContent>
        )}
        {confirmDelete && confirmDelete.provider === 'azure' && (
          <DialogContent dividers>
            <Typography sx={{ mb: 2 }}>
              Remove this workspace’s Azure connection? Primordia will stop looking for subscriptions
              delegated from tenant <b>{confirmDelete.payerAccountId || confirmDelete.id}</b>.
            </Typography>
            <Alert severity="warning">
              This only removes Primordia’s record of the connection. To fully revoke the delegation
              itself, delete the <b>Optimum Primordia FinOps</b> offer under <b>Service providers &rarr;
              Service provider offers</b> in your own Azure portal — revocation is entirely in your hands.
            </Alert>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={!!busy}>Cancel</Button>
          <Button
            color="error" variant="contained" onClick={disconnect}
            disabled={!!busy}
          >
            {busy && busy.startsWith('delete:') ? 'Disconnecting…' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
